/**
 * TemplateStrip — THE one home for saved templates on the pad (D20, variant C).
 *
 *   ⧉ Templates (i) | [Whole prescription · 11][Symptoms · 6][Medicines · 3]…
 *                     [+ URI kit] [+ Fever meds] [See all 11] [Save…]
 *
 * One labelled, tinted block directly under the vitals line. It never moves and
 * never grows with the data: four chips at most, everything else behind
 * `See all n` (the existing portalled TemplatePickerList).
 *
 * EXPLICIT REVERSAL of D14 — the five per-section chip groups are gone from the
 * pad; templates stay reachable field-by-field through the D10 typing
 * suggestions, which are untouched. D14 AC5 is softened here: a category with
 * nothing saved keeps a visible tab reading `· 0`, because that tab is what
 * teaches a new doctor the five kinds exist (and carries the `Save…` that
 * creates the first one).
 *
 * Every count and ordering comes from the shared `selectByCategory` selection
 * passed in — this component never sorts or counts anything itself, and it
 * renders `TemplateChipRow` for its body rather than re-implementing chips.
 */
import React, { useLayoutEffect, useRef, useState } from 'react';
import TemplateChipRow, { NARROW_HOST_PX } from './TemplateChipRow';
import { SectionHeader } from './PrescriptionHelpers';
import {
  TEMPLATE_CATEGORY_KEYS,
  TEMPLATE_CATEGORY_EMPTY_COPY,
  TEMPLATE_ROW_COPY,
  TEMPLATE_STRIP_COPY,
  TEMPLATE_ERROR_COPY,
  WHOLE_PRESCRIPTION,
} from '../../utils/rxTemplates';

/** Two offset rounded rectangles — the same glyph on every platform. */
function TemplatesIcon({ color }) {
  return (
    <svg width="13" height="13" viewBox="0 0 16 16" aria-hidden="true" focusable="false"
      style={{ display: 'block', flexShrink: 0 }}>
      <rect x="1.2" y="3.6" width="9" height="11" rx="1.6" fill="none" stroke={color} strokeWidth="1.4" />
      <path d="M4.6 1.4h7.6a2 2 0 0 1 2 2v8.2" fill="none" stroke={color} strokeWidth="1.4" strokeLinecap="round" />
    </svg>
  );
}

export default function TemplateStrip({
  selection,
  onAdd,
  onOpenAll,
  onSave,
  // (category) => { disabled, title } — asked per ACTIVE tab, because only this
  // component knows which tab that is. Same pure helper the section rows used.
  saveAffordanceFor,
  isDark = false,
  initialCategory = WHOLE_PRESCRIPTION,
  // D2 (QA r1) — the list FETCH FAILED. A doctor who may hold 200 templates
  // must never be told "Nothing saved yet": the strip is where he is looking,
  // so the ratified failure line and its `Try again` belong HERE, not only
  // inside a list he cannot open (the opener needs a count > 0).
  error = null,
  onRetry = null,
  // D30 — which category the All-templates list is open for (null = closed),
  // so the opener that opened it can show it.
  openCategory = null,
  // D22(b) — pin the strip while the pad scrolls, so the tabs, `See all n` and
  // `Save…` stay in reach from the bottom of a long prescription. Off for a
  // host that cannot pin (see the measure below).
  sticky = true,
}) {
  const [active, setActive] = useState(initialCategory);

  // ONE measure for the whole strip; the body is told what it found (it must
  // not measure a second time — the tab bar changes its own width).
  const hostRef = useRef(null);
  const [narrow, setNarrow] = useState(false);
  useLayoutEffect(() => {
    const measure = () => {
      const w = hostRef.current?.getBoundingClientRect().width || 0;
      setNarrow((prev) => {
        const next = w > 0 && w < NARROW_HOST_PX;
        return prev === next ? prev : next;
      });
    };
    measure();
    window.addEventListener('resize', measure);
    return () => window.removeEventListener('resize', measure);
  }, []);

  // ── D22(b) sticky ───────────────────────────────────────────────────
  // Where the strip pins depends on WHO scrolls, and that differs per mount —
  // so it is MEASURED, never hardcoded:
  //   · a scrolling ancestor (Smart Workflow panel, a modal body) → top: 0,
  //     the strip pins to that box;
  //   · the page itself → pin under whatever is `position: fixed` across the
  //     top of the viewport (the app header), measured through the element at
  //     the top edge; 0 when there is nothing there.
  // A narrow host (the Visit Actions accordion, mobile) is left alone: the
  // strip is three lines there and pinning it would eat the pad.
  const [stickyTop, setStickyTop] = useState(null);
  const [stuck, setStuck] = useState(false);
  const canPin = sticky && !narrow;

  useLayoutEffect(() => {
    if (!canPin) { setStickyTop(null); return undefined; }
    const el = hostRef.current;
    if (!el) return undefined;

    const scrollHost = () => {
      let node = el.parentElement;
      while (node && node !== document.body) {
        const overflowY = window.getComputedStyle?.(node)?.overflowY;
        if (overflowY === 'auto' || overflowY === 'scroll') return node;
        node = node.parentElement;
      }
      return null;
    };
    const host = scrollHost();

    const measureTop = () => {
      if (host) return 0;                       // pins inside its own scroll box
      // The page scrolls: sit under the fixed app chrome at the top edge.
      let probe = null;
      try {
        probe = document.elementFromPoint?.(Math.floor((window.innerWidth || 0) / 2), 1) || null;
      } catch { probe = null; }
      while (probe && probe !== document.body) {
        const pos = window.getComputedStyle?.(probe)?.position;
        if (pos === 'fixed' || pos === 'sticky') {
          const bottom = probe.getBoundingClientRect().bottom;
          return bottom > 0 && bottom < (window.innerHeight || 0) / 2 ? Math.round(bottom) : 0;
        }
        probe = probe.parentElement;
      }
      return 0;
    };

    const place = () => {
      const top = measureTop();
      setStickyTop((prev) => (prev === top ? prev : top));
      // "Stuck" = something has actually scrolled AND the strip has reached its
      // pin line. The scrolled check matters: an unscrolled host (and jsdom,
      // where every rect is 0) must NOT read as pinned, or the strip would open
      // collapsed. One passive listener on whoever scrolls; no observer, no
      // polling.
      const scrolled = host ? host.scrollTop > 0 : (window.scrollY || 0) > 0;
      const rect = el.getBoundingClientRect();
      setStuck((prev) => {
        const next = scrolled && rect.top <= top + 1;
        return prev === next ? prev : next;
      });
    };
    place();
    const target = host || window;
    target.addEventListener('scroll', place, { passive: true });
    window.addEventListener('resize', place);
    return () => {
      target.removeEventListener('scroll', place);
      window.removeEventListener('resize', place);
    };
  }, [canPin]);

  const total = selection?.total ?? 0;
  const counts = selection?.counts || {};

  // ── surface (solid in BOTH modes — day-mode contrast rule) ──────────
  const surface = isDark ? '#1a2333' : '#f1f5f9';
  const border = isDark ? 'rgba(255,255,255,0.10)' : 'rgba(15,23,42,0.10)';
  const ink = isDark ? '#e2e8f0' : '#334155';
  const inkFaint = isDark ? '#94a3b8' : '#64748b';
  const teal = isDark ? '#5eead4' : '#0d9488';

  const tabStyle = (isActive) => ({
    padding: '0.2rem 0.55rem', borderRadius: '0.375rem', cursor: 'pointer',
    fontSize: '0.8125rem', fontWeight: 600, whiteSpace: 'nowrap', flexShrink: 0,
    fontFamily: 'system-ui, sans-serif',
    // Never bg-transparent on an inactive leg (transparency / inactive-state rule)
    border: `1px solid ${isActive ? '#0d9488' : (isDark ? 'rgba(255,255,255,0.22)' : '#cbd5e1')}`,
    background: isActive ? '#0d9488' : (isDark ? '#1e293b' : '#ffffff'),
    color: isActive ? '#ffffff' : ink,
  });

  return (
    <div
      ref={hostRef}
      data-testid="template-strip"
      role="region"
      aria-label={TEMPLATE_STRIP_COPY.regionAriaLabel}
      data-stuck={canPin && stuck ? 'true' : undefined}
      style={{
        width: '100%', boxSizing: 'border-box',
        display: 'flex', flexDirection: 'column', gap: '0.3rem',
        padding: '0.4rem 0.6rem',
        background: surface,
        border: `1px solid ${border}`,
        borderLeft: `3px solid ${teal}`,
        borderRadius: '0.375rem',
        fontFamily: 'system-ui, sans-serif',
        ...(canPin && stickyTop !== null ? {
          position: 'sticky',
          top: stickyTop,
          zIndex: 20,
          // Pinned over the prescription it sits on: it must stay opaque and
          // keep its edge in both modes.
          boxShadow: stuck ? '0 4px 12px rgba(0,0,0,0.16)' : 'none',
        } : null),
      }}
    >
      {/* line 1 — identity + tabs. On a narrow host the label keeps its own
          line and the tabs WRAP under it; never a sideways scroller. */}
      <div role="tablist" aria-label={TEMPLATE_STRIP_COPY.tabListAriaLabel}
        style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', flexWrap: 'wrap' }}>
        <SectionHeader
          icon={<TemplatesIcon color={teal} />}
          label={TEMPLATE_STRIP_COPY.heading}
          tooltip={TEMPLATE_ROW_COPY.sectionTooltipSentence}
          isDark={isDark}
        />
        {total > 0 && TEMPLATE_CATEGORY_KEYS.map((key) => (
          <button
            key={key}
            type="button"
            role="tab"
            aria-selected={active === key}
            aria-label={TEMPLATE_STRIP_COPY.tabAriaLabel(key, counts[key] || 0)}
            onClick={() => setActive(key)}
            style={tabStyle(active === key)}
          >{TEMPLATE_STRIP_COPY.tabLabel(key, counts[key] || 0)}</button>
        ))}
        {/* First use: no tabs to count, one sentence instead. A FAILED fetch
            is a different state and says so (S2 AC8). */}
        {total === 0 && !error && (
          <span style={{ fontSize: '0.75rem', color: inkFaint, maxWidth: '34rem' }}>
            {TEMPLATE_ROW_COPY.zeroLine}
          </span>
        )}
        {error && (
          <span style={{ fontSize: '0.75rem', color: ink, maxWidth: '34rem' }}>
            {TEMPLATE_ERROR_COPY.line}
          </span>
        )}
      </div>

      {/* line 2 — the active tab's body: chips + See all n + Save… */}
      <div style={{ display: 'flex', alignItems: 'center', gap: '0.3rem', flexWrap: 'wrap' }}>
        {error && onRetry && (
          <button
            type="button"
            onClick={onRetry}
            style={{
              padding: '0.25rem 0.6rem', borderRadius: '2rem', cursor: 'pointer',
              fontSize: '0.8125rem', fontWeight: 600, whiteSpace: 'nowrap',
              border: `1px solid ${isDark ? 'rgba(255,255,255,0.24)' : '#94a3b8'}`,
              background: isDark ? '#1e293b' : '#ffffff',
              color: ink,
            }}
          >{TEMPLATE_ERROR_COPY.retry}</button>
        )}
        {total > 0 && (counts[active] || 0) === 0 && (
          <span style={{ fontSize: '0.75rem', color: inkFaint }}>
            {TEMPLATE_CATEGORY_EMPTY_COPY[active]}
          </span>
        )}
        <TemplateChipRow
          // Stuck, the strip is chrome over the doctor's own words — it drops
          // to one line: tabs, `See all n` and `Save…`, no chips.
          narrow={canPin && stuck ? true : narrow}
          category={total === 0 ? WHOLE_PRESCRIPTION : active}
          selection={selection}
          onAdd={onAdd}
          onOpenAll={onOpenAll}
          onSave={onSave}
          saveAffordance={saveAffordanceFor(total === 0 ? WHOLE_PRESCRIPTION : active)}
          openCategory={openCategory}
          isDark={isDark}
          variant="strip"
          alwaysShow
        />
      </div>
    </div>
  );
}
